import React, { useState } from 'react'
import { TimelineChangePoint } from '../../services/api'
import { formatToPacificTime } from '../../utils/timezone'
import styles from './TimelineScrubber.module.scss'

/** Sentinel value for the "After publish" scrubber point */
export const AFTER_PUBLISH = '__after_publish__'

interface Props {
  changePoints: TimelineChangePoint[]
  now: string
  hasDraft: boolean
  /** null = "now" (live), AFTER_PUBLISH = staged/draft view, string = future timestamp */
  activeTimestamp: string | null
  onTimestampChange: (timestamp: string | null) => void
}

/** Short date/time label for axis markings */
const formatAxisLabel = (iso: string): string => {
  const d = new Date(iso)
  const now = new Date()
  const isToday = d.toDateString() === now.toDateString()
  const tomorrow = new Date(now)
  tomorrow.setDate(tomorrow.getDate() + 1)
  const isTomorrow = d.toDateString() === tomorrow.toDateString()

  const time = d.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    timeZone: 'America/Los_Angeles',
  })

  if (isToday) return `Today ${time}`
  if (isTomorrow) return `Tomorrow ${time}`

  const date = d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    timeZone: 'America/Los_Angeles',
  })
  return `${date} ${time}`
}

const TimelineScrubber: React.FC<Props> = ({ changePoints, now, hasDraft, activeTimestamp, onTimestampChange }) => {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null)
  const [hoveredNow, setHoveredNow] = useState(false)
  const [hoveredDraft, setHoveredDraft] = useState(false)

  // Build the list of points: Now, [After publish], change points...
  const draftSlots = hasDraft ? 1 : 0
  const totalSlots = 1 + draftSlots + changePoints.length
  const getNowPct = () => (0.5 / totalSlots) * 100
  const getDraftPct = () => (1.5 / totalSlots) * 100
  const getTickPct = (i: number) => ((i + 1.5 + draftSlots) / totalSlots) * 100

  // Nothing to show if no change points and no draft
  if (changePoints.length === 0 && !hasDraft) {
    return null
  }

  const isNowActive = activeTimestamp === null
  const isDraftActive = activeTimestamp === AFTER_PUBLISH

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <span className={styles.label}>Timeline</span>
        {activeTimestamp && (
          <button className={styles.liveButton} onClick={() => onTimestampChange(null)}>
            Back to Live
          </button>
        )}
        <span className={styles.currentTime}>
          {isNowActive && 'Live (now)'}
          {isDraftActive && 'After publish'}
          {activeTimestamp && activeTimestamp !== AFTER_PUBLISH && `Previewing: ${formatToPacificTime(activeTimestamp)}`}
          {activeTimestamp && activeTimestamp !== AFTER_PUBLISH && (() => {
            const cp = changePoints.find(c => c.timestamp === activeTimestamp)
            return cp?.events.some(e => !e.is_live)
          })() && <span className={styles.draftNote}> — includes unpublished changes</span>}
        </span>
      </div>
      <div className={styles.track}>
        {/* Now marker */}
        <div
          className={styles.tickMarker}
          style={{ left: `${getNowPct()}%` }}
          onClick={() => onTimestampChange(null)}
          onMouseEnter={() => setHoveredNow(true)}
          onMouseLeave={() => setHoveredNow(false)}
        >
          <div className={`${styles.dot} ${styles.nowDot} ${isNowActive ? styles.activeDot : ''}`} />
          <span className={styles.axisLabel}>Now</span>
          {hoveredNow && !isNowActive && (
            <div className={styles.tooltip}>
              <div className={styles.tooltipTime}>Return to live view</div>
            </div>
          )}
        </div>

        {/* After publish marker */}
        {hasDraft && (
          <div
            className={styles.tickMarker}
            style={{ left: `${getDraftPct()}%` }}
            onClick={() => onTimestampChange(AFTER_PUBLISH)}
            onMouseEnter={() => setHoveredDraft(true)}
            onMouseLeave={() => setHoveredDraft(false)}
          >
            <div className={`${styles.dot} ${styles.draftDot} ${isDraftActive ? styles.activeDot : ''}`} />
            <span className={styles.axisLabel}>After publish</span>
            {hoveredDraft && !isDraftActive && (
              <div className={styles.tooltip}>
                <div className={styles.tooltipTime}>Preview with pending changes applied</div>
              </div>
            )}
          </div>
        )}

        {/* Change point ticks */}
        {changePoints.map((tick, i) => {
          const isActive = activeTimestamp === tick.timestamp
          const isHovered = hoveredIndex === i

          return (
            <div
              key={tick.timestamp}
              className={styles.tickMarker}
              style={{ left: `${getTickPct(i)}%` }}
              onMouseEnter={() => setHoveredIndex(i)}
              onMouseLeave={() => setHoveredIndex(null)}
              onClick={() => onTimestampChange(tick.timestamp)}
            >
              <div className={`${styles.dot} ${isActive ? styles.activeDot : ''}`} />
              <span className={styles.axisLabel}>{formatAxisLabel(tick.timestamp)}</span>

              {/* Tooltip on hover */}
              {isHovered && (
                <div className={styles.tooltip}>
                  <div className={styles.tooltipTime}>{formatToPacificTime(tick.timestamp)}</div>
                  {tick.events.map((ev, j) => (
                    <div key={j} className={styles.tooltipEvent}>
                      <span className={ev.event_type === 'starts' ? styles.eventStarts : styles.eventEnds}>
                        {ev.event_type === 'starts' ? '▶' : '■'}
                      </span>
                      {' '}{ev.placement_name}
                      <span className={styles.tooltipMeta}> ({ev.region_name} #{ev.position + 1})</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )
        })}

        {/* Connecting line between dots */}
        <div className={styles.connectingLine} />
      </div>
    </div>
  )
}

export default TimelineScrubber
